/**
 * @module
 *
 * @import {Controller} from "@acng/frontend-stargazer"
 * @import {UserProfile} from "./profile.js"
 * @import {PreferenceName, Option} from "../model/user-profile.js"
 */
import {COMPILE, CONNECT, SCOPE, UPGRADE} from '@acng/frontend-stargazer';
import {push} from '@acng/frontend-bounty/std/array.js';
import {guard, IS} from '@acng/frontend-rubicon';
import {all} from '@acng/frontend-bounty/std/control.js';
import {CHANGE, on} from '@acng/frontend-bounty/dom/event.js';
import {SubmitEventController} from 'nova';

import {put} from '../../core/service/backend.js';
import {getPreferences} from '../model/user-profile.js';
import {user} from '../user.js';

/**
 * @implements {Controller}
 */
class Preference extends SubmitEventController {
  [SCOPE] = this;

  /**
   * @readonly
   */
  #profile;

  /**
   * @type {PreferenceName}
   *
   * @readonly
   */
  #type;

  /**
   * The options include translations and therefore are loaded in the
   * {@link COMPILE} phase.
   *
   * @type {Option[]}
   */
  repeat = [];

  /**
   * @param {UserProfile} profile
   * @param {PreferenceName} type
   */
  constructor(profile, type) {
    super();
    this.#profile = profile;
    this.#type = type;
  }

  async [COMPILE]() {
    const {lang, adult, guest} = user;

    if (guest || !adult) {
      return null;
    }

    const [list] = await all([getPreferences(lang)(this.#type), this.#profile.get()]);

    this.repeat = list;
  }

  /**
   * @param {Element} form
   */
  [UPGRADE](form) {
    ASSERT: guard(form, IS(HTMLFormElement));
    on(form, CHANGE, () => form.requestSubmit());
  }

  /**
   * @param {Element} form
   */
  [CONNECT](form) {
    super[CONNECT](form);
    ASSERT: guard(form, IS(HTMLFormElement));

    const input = form.elements;
    const profileData = this.#profile.data;

    for (const i of input) {
      ASSERT: guard(i, IS(HTMLInputElement));
      i.checked = profileData[this.#type].includes(parseInt(i.value));
    }
  }

  /**
   * @param {HTMLFormElement} form
   */
  async submit(form) {
    const type = this.#type;
    /**
     * @type {string[]}
     */
    const binaries = [];
    const input = form.elements;

    for (const i of input) {
      ASSERT: guard(i, IS(HTMLInputElement));
      if (i.checked) {
        push(binaries, i.value);
      }
    }

    this.#profile.data[type] = binaries.map((v) => parseInt(v));

    await put('userProfile/preferences', {type, binaries}, form);
  }
}

export {Preference};
